<script setup>
import { ref } from "vue";
import { useMenu } from "../../composables/useMenu.js";
import { downloadBase64File } from "../../composables/useFileDownload.js";
import ProjectService from "../../services/projectService.js";

const props = defineProps({
  documentId: { type: String, required: true },
  documentName: { type: String, default: "Document" },
  // Gates the two sections below — same permissions DocumentCardMenu's
  // download options use (DOWNLOAD_ANNOTATED_DOCUMENT / DOWNLOAD_SOURCE_DOCUMENT
  // + DOWNLOAD_ORIGINAL_DOCUMENT), computed by the caller from the current
  // document's permissions.
  canDownloadAnnotated: { type: Boolean, default: false },
  canDownloadOriginal: { type: Boolean, default: false },
});

const projectService = new ProjectService();
const { isOpen, triggerEl, menuEl, menuStyle, toggle, close, onMenuKeydown } = useMenu();

// Tracks which download is in flight, not just whether one is — see
// DocumentCardMenu's onDownload for why (closing the popup shouldn't block a
// second, different-kind download from starting while the first finishes).
const downloadingKind = ref(null); // null | "original" | "printable"
async function onDownload(original) {
  const kind = original ? "original" : "printable";
  if (downloadingKind.value === kind) return;
  downloadingKind.value = kind;
  try {
    const { content, originMimeType } = await projectService.downloadDocument({
      documentId: props.documentId,
      flatten: !original,
      original,
    });
    if (content) downloadBase64File(content, originMimeType, props.documentName ?? "document");
  } finally {
    downloadingKind.value = null;
    close({ returnFocus: false });
  }
}
</script>

<template>
  <div v-if="canDownloadAnnotated || canDownloadOriginal" class="relative shrink-0">
    <button
      ref="triggerEl"
      class="flex items-center justify-center w-9 h-9 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer"
      aria-haspopup="menu"
      :aria-expanded="isOpen"
      aria-label="Download document"
      title="Download"
      @click="toggle"
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        ref="menuEl"
        role="menu"
        aria-label="Download document"
        :style="menuStyle"
        class="w-[360px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-xl z-[400] overflow-hidden"
        @keydown="onMenuKeydown"
      >
        <div class="flex items-center justify-between px-5 py-4">
          <h2 class="text-lg font-bold text-slate-800 dark:text-slate-100 m-0">Download</h2>
          <button
            class="flex items-center justify-center w-7 h-7 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-600 dark:hover:text-slate-200 border-0 bg-transparent cursor-pointer transition-colors"
            aria-label="Close"
            @click="close()"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <div class="h-px bg-slate-200 dark:bg-slate-700" />

        <div class="px-5 py-4">
          <div class="flex items-center gap-1.5 mb-3">
            <span class="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{{ documentName }}</span>
            <svg class="text-slate-400 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
              <path d="M12 11v5M12 8h.01" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
            </svg>
          </div>

          <template v-if="canDownloadAnnotated">
            <p class="text-sm text-slate-500 dark:text-slate-400 m-0 mb-3">Download a PDF with all annotations embedded and uneditable :</p>
            <button
              role="menuitem"
              :disabled="downloadingKind === 'printable'"
              class="flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-slate-800 dark:bg-slate-700 text-white text-[15px] font-bold hover:bg-slate-700 dark:hover:bg-slate-600 transition-colors cursor-pointer border-0 disabled:opacity-50"
              @click="onDownload(false)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ downloadingKind === "printable" ? "Downloading…" : "Download flattened PDF" }}
            </button>
          </template>

          <div v-if="canDownloadAnnotated && canDownloadOriginal" class="h-px bg-slate-200 dark:bg-slate-700 -mx-5 my-4" />

          <p v-if="canDownloadOriginal" class="text-sm text-slate-500 dark:text-slate-400 m-0">
            To download the original file without annotations,
            <button
              role="menuitem"
              :disabled="downloadingKind === 'original'"
              class="text-blue-500 dark:text-blue-400 font-semibold underline border-0 bg-transparent p-0 cursor-pointer disabled:opacity-50"
              @click="onDownload(true)"
            >{{ downloadingKind === "original" ? "Downloading…" : "Click here" }}</button>
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>
